import { Injectable, inject } from '@angular/core';
import _ from 'lodash';

import { THEMES_HELPER_PORT } from './themes-helper.port';

const EMAIL_REGEX = /(^(?!\s))[A-Z0-9a-z._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*(\.[A-Za-z]{2,})$/;

function isEmptyInputValue(value: unknown): boolean {
  return value === undefined || value === null || (value as { length?: number }).length === 0;
}

function emailValidator(value: string): boolean {
  return EMAIL_REGEX.test(value);
}

function phoneValidator(value: string): boolean {
  const stripped = value ? value.replace(/\D+/g, '') : '';
  return /^\d{9,100}$/.test(stripped);
}

export interface ACMEValidatorTest {
  field: string;
  [key: string]: unknown;
}

export interface ACMEValidationError {
  validator: string | false;
  message: string;
  payload?: ACMEValidatorTest;
}

type ValidatorFn = (value: unknown) => boolean;

@Injectable({ providedIn: 'root' })
export class ValidateService {
  private readonly themesHelper = inject(THEMES_HELPER_PORT);

  tests: ACMEValidatorTest[] = [];
  errors: ACMEValidationError[] = [];
  passed: unknown[] = [];

  init(tests?: ACMEValidatorTest[]): boolean {
    if (tests instanceof Array) {
      this.tests = tests;
    }
    return true;
  }

  validate(list: ACMEValidatorTest[]): boolean {
    this.errors = [];
    this.passed = [];

    for (let i = 0, ii = list.length; i < ii; i++) {
      const keys: string[] = [];
      let validatorFunc: ValidatorFn | false = false;
      let customMessage: string | false = false;
      let validatorName: string | false = false;
      let validatorValue: unknown = false;
      let validatorObject: { message?: string; test?: unknown } | false = false;

      for (const key in list[i]) {
        if (Object.prototype.hasOwnProperty.call(list[i], key)) {
          if (key === 'field') {
            continue;
          }
          keys.push(key);
          break;
        }
      }

      if (keys.length === 1) {
        validatorName = keys[0];
        const validatorObjectRaw = list[i][validatorName] as { message?: string; test?: unknown } | false;
        validatorObject = validatorObjectRaw;

        if (list[i][validatorName]) {
          if (validatorObjectRaw && Object.prototype.hasOwnProperty.call(validatorObjectRaw, 'message')) {
            customMessage = validatorObjectRaw.message as string;
            if (Object.prototype.hasOwnProperty.call(validatorObjectRaw, 'test')) {
              validatorValue =
                typeof validatorObjectRaw.test === 'function'
                  ? (validatorObjectRaw.test as () => unknown)()
                  : validatorObjectRaw.test;
            } else {
              validatorValue = false;
              this.errors.push({
                validator: validatorName,
                message: 'No test to run for validator `' + validatorName + '`',
              });
            }
          } else {
            validatorValue = list[i][validatorName];
          }
        }

        validatorFunc = this.getValidator(validatorName);

        if (validatorFunc && !validatorFunc(validatorValue)) {
          this.errors.push({
            validator: validatorName,
            message: customMessage ? customMessage : 'Invalid `' + validatorName + '`',
            payload: list[i],
          });
        } else if (!validatorFunc) {
          this.errors.push({
            validator: validatorName,
            message: 'No validator `' + validatorName + '` exists',
            payload: list[i],
          });
        } else {
          this.passed.push((list[i] as { field: unknown }).field);
        }
      } else {
        this.errors.push({
          validator: false,
          message: 'Validator not found.',
          payload: list[i],
        });
      }
    }

    return this.errors.length === 0;
  }

  getValidator(name: string): ValidatorFn | false {
    const validators = this.validators as Record<string, ValidatorFn>;
    if (Object.prototype.hasOwnProperty.call(validators, name)) {
      return validators[name];
    }
    return false;
  }

  getPasswordMessage(): string {
    let passwordMessage = 'The password must be at least 8 characters long';

    if (this.themesHelper.getCompany('passwordSecurityPolicyEnabled')) {
      const minLength = this.themesHelper.getCompany('passwordMinimumLength');
      if (minLength) {
        passwordMessage = 'The password must be at least ' + minLength + ' characters long';
      }

      if (this.themesHelper.getCompany('passwordUseMixedCase')) {
        passwordMessage = passwordMessage + ', use a mix of lower case and capital letters';
      }

      if (this.themesHelper.getCompany('passwordMinimumDigits')) {
        passwordMessage = passwordMessage + ', at least one number';
      }

      if (this.themesHelper.getCompany('passwordMinimumSymbols')) {
        passwordMessage = passwordMessage + ', at least one special character like _, !, # etc.';
      }
    }

    return passwordMessage;
  }

  getError(fieldName: string): ACMEValidationError | false {
    for (let i = 0, ii = this.errors.length; i < ii; i++) {
      const err = this.errors[i];
      if (err.payload && err.payload.field === fieldName) {
        return err;
      }
    }
    return false;
  }

  getErrorHash(): Record<string, ACMEValidationError> {
    const r: Record<string, ACMEValidationError> = {};
    for (let i = 0, ii = this.errors.length; i < ii; i++) {
      const err = this.errors[i];
      if (err.payload) {
        r[err.payload.field] = err;
      }
    }
    return r;
  }

  check(list: ACMEValidatorTest[], fieldName: string): boolean {
    const errors: ACMEValidationError[] = [];

    for (let i = 0, ii = list.length; i < ii; i++) {
      const keys: string[] = [];
      let validatorFunc: ValidatorFn | false = false;
      let customMessage: string | false = false;
      let validatorName: string | false = false;
      let validatorValue: unknown = false;

      if (list[i].field !== fieldName) {
        continue;
      }

      for (const key in list[i]) {
        if (Object.prototype.hasOwnProperty.call(list[i], key)) {
          if (key === 'field') {
            continue;
          }
          keys.push(key);
          break;
        }
      }

      if (keys.length === 1) {
        validatorName = keys[0];

        if (list[i][validatorName]) {
          const valObj = list[i][validatorName] as {
            message?: string;
            test?: unknown;
          };
          if (Object.prototype.hasOwnProperty.call(valObj, 'message')) {
            customMessage = valObj.message as string;
            if (Object.prototype.hasOwnProperty.call(valObj, 'test')) {
              validatorValue = typeof valObj.test === 'function' ? (valObj.test as () => unknown)() : valObj.test;
            } else {
              validatorValue = false;
              errors.push({
                validator: validatorName,
                message: 'No test to run for validator `' + validatorName + '`',
              });
            }
          } else {
            validatorValue = list[i][validatorName];
          }
        }

        validatorFunc = this.getValidator(validatorName);

        if (validatorFunc && !validatorFunc(validatorValue)) {
          errors.push({
            validator: validatorName,
            message: customMessage ? customMessage : 'Invalid `' + validatorName + '`',
            payload: list[i],
          });
        } else if (!validatorFunc) {
          errors.push({
            validator: validatorName,
            message: 'No validator `' + validatorName + '` exists',
            payload: list[i],
          });
        }
      } else {
        errors.push({
          validator: false,
          message: 'Validator not found.',
          payload: list[i],
        });
      }
    }

    return errors.length === 0;
  }

  findError(validators: Array<{ field: string; message: string }>, fieldName: string): string | false {
    for (let i = 0, ii = validators.length; i < ii; i++) {
      if (validators[i].field === fieldName) {
        return validators[i].message;
      }
    }
    return false;
  }

  addFieldTest(test: ACMEValidatorTest): void {
    const dupe = this.findDupe(test);
    if (!dupe) {
      // Legacy used angular.copy(test), which deep-copies data but keeps function
      // properties by reference. Validator tests carry a `test` function, so
      // structuredClone() throws DataCloneError on them — clone deep but preserve
      // functions as-is to match the original behaviour.
      this.tests.push(
        _.cloneDeepWith(test, (value) => (typeof value === 'function' ? value : undefined)) as ACMEValidatorTest,
      );
    } else {
      this.tests[dupe.index] = test;
    }
  }

  addFieldTests(tests: ACMEValidatorTest[]): void {
    if (tests instanceof Array) {
      for (let i = 0, ii = tests.length; i < ii; i++) {
        this.addFieldTest(tests[i]);
      }
    }
  }

  removeFieldTest(test: ACMEValidatorTest): void {
    for (let ii = 0, i = this.tests.length - 1; i >= ii; i--) {
      if (this.tests[i].field === test.field) {
        this.tests.splice(i, 1);
        break;
      }
    }
  }

  clearFieldTests(): void {
    this.tests = [];
  }

  runFieldTests(): void {
    this.validate(this.tests);
  }

  findDupe(test: ACMEValidatorTest): { test: ACMEValidatorTest; index: number } | false {
    for (let ii = 0, i = this.tests.length - 1; i >= ii; i--) {
      if (this.tests[i].field === test.field) {
        return { test: this.tests[i], index: i };
      }
    }
    return false;
  }

  validators = {
    email: (value: string): boolean => emailValidator(value),

    optionalEmail: (value: string): boolean => {
      if (isEmptyInputValue(value)) {
        return true;
      }
      return emailValidator(value);
    },

    notEmail: (value: string): boolean => {
      return !/(^(?!\s))[-0-9a-zA-Z.+_']+@{1}[-0-9a-zA-Z.+_]+\.[a-zA-Z]{2,4}$/.test(value);
    },

    password: (value: string): boolean => {
      const policies = {
        passwordSecurityPolicyEnabled: this.themesHelper.getCompany('passwordSecurityPolicyEnabled'),
        passwordMinimumLength: this.themesHelper.getCompany('passwordMinimumLength') as number | undefined,
        passwordUseMixedCase: this.themesHelper.getCompany('passwordUseMixedCase'),
        passwordMinimumDigits: this.themesHelper.getCompany('passwordMinimumDigits'),
        passwordMinimumSymbols: this.themesHelper.getCompany('passwordMinimumSymbols'),
      };

      if (!policies.passwordMinimumLength || policies.passwordMinimumLength < 8) {
        policies.passwordMinimumLength = 8;
      }

      let r: boolean = !!(value && value.length >= policies.passwordMinimumLength);

      if (policies.passwordSecurityPolicyEnabled) {
        if (policies.passwordUseMixedCase) {
          r = r && /^(?=.*[a-z])(?=.*[A-Z]).+$/.test(value);
        }
        if (policies.passwordMinimumDigits) {
          r = r && /^(?=.*\d).+$/.test(value);
        }
        if (policies.passwordMinimumSymbols) {
          r = r && /^(?=.*[_\W]).+$/.test(value);
        }
      }

      return r;
    },

    name: (value: string): boolean => {
      const testValue = value ? value : '';
      return /[a-zA-Z\s]+/.test(testValue) && /^[\d]+/g.test(testValue) === false;
    },

    number: (value: unknown): boolean => !isNaN(parseFloat(value as string)) && isFinite(value as number),

    positive: (value: unknown): boolean =>
      !isNaN(parseFloat(value as string)) && isFinite(value as number) && (value as number) > 0,

    greaterThan: (props: { actual: number; target: number }): boolean =>
      !isNaN(parseFloat(props.actual as unknown as string)) && isFinite(props.actual) && props.actual > props.target,

    oneOrGreater: (value: unknown): boolean =>
      !isNaN(parseFloat(value as string)) && isFinite(value as number) && (value as number) >= 1,

    negative: (value: unknown): boolean =>
      !isNaN(parseFloat(value as string)) && isFinite(value as number) && (value as number) < 0,

    utcDate: (value: string): boolean => {
      const testValue = value ? value : '';
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?((-|\+)\d{2}:\d{2})?$/.test(testValue);
    },

    zip: (value: string | number): boolean => {
      const testValue = value ? String(value) : '';
      return /^\d{5}(-\d{4})?$/.test(testValue);
    },

    zipcode: (props: { value?: string; country?: string; required?: boolean }): boolean => {
      const value = props.value || '';
      const country = props.country || '';
      const required = props.required || false;
      let regex: RegExp;

      switch (country.toLowerCase()) {
        case 'canada':
          regex = new RegExp('^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] +[0-9][A-Z][0-9]$', 'g');
          break;
        default:
          regex = new RegExp('^\\d{5}(-\\d{4})?$');
      }

      return (!required && !value.length) || (required && !!value.length && !country.length) || regex.test(value);
    },

    greater: (props: { greater: unknown; less: unknown; equalTo?: boolean }): boolean => {
      const p = props as {
        greater?: unknown;
        less?: unknown;
        equalTo?: boolean;
      };
      if ((isNaN(parseFloat(p.greater as string)) && !isFinite(p.greater as number)) || p.greater == null) {
        delete p.greater;
      } else if (typeof p.greater === 'string' && p.greater.replace(/s+/, '').length === 0) {
        delete p.greater;
      }
      if ((isNaN(parseFloat(p.less as string)) && !isFinite(p.less as number)) || p.less == null) {
        delete p.less;
      } else if (typeof p.less === 'string' && p.less.replace(/s+/, '').length === 0) {
        delete p.less;
      }

      return p.equalTo ? (p.greater as number) >= (p.less as number) : (p.greater as number) > (p.less as number);
    },

    less: (props: { greater: unknown; less: unknown; equalTo?: boolean }): boolean => {
      const p = props as {
        greater?: unknown;
        less?: unknown;
        equalTo?: boolean;
      };
      if ((isNaN(parseFloat(p.greater as string)) && !isFinite(p.greater as number)) || p.greater == null) {
        delete p.greater;
      } else if (typeof p.greater === 'string' && p.greater.replace(/s+/, '').length === 0) {
        delete p.greater;
      }
      if ((isNaN(parseFloat(p.less as string)) && !isFinite(p.less as number)) || p.less == null) {
        delete p.less;
      } else if (typeof p.less === 'string' && p.less.replace(/s+/, '').length === 0) {
        delete p.less;
      }

      return p.equalTo ? (p.greater as number) <= (p.less as number) : (p.greater as number) < (p.less as number);
    },

    phone: (value: string): boolean => phoneValidator(value),

    optionalPhone: (value: string): boolean => {
      if (isEmptyInputValue(value)) {
        return true;
      }
      return phoneValidator(value);
    },

    predicate: (props: { predicateFn: () => boolean }): boolean => props.predicateFn(),

    equal: (values: [unknown, unknown]): boolean => values[0] === values[1],

    notEqual(values: [unknown, unknown]): boolean {
      return this.equal(values) === false;
    },

    or: (props: { values?: unknown[]; expected?: unknown }): boolean => {
      if (props.values && props.values instanceof Array && Object.prototype.hasOwnProperty.call(props, 'expected')) {
        for (let i = 0, ii = props.values.length; i < ii; i++) {
          if (props.values[i] === props.expected) {
            return true;
          }
        }
      }
      return false;
    },

    oneOf: (props: { values: Array<{ value: unknown }> }): boolean => props.values.some((item) => !!item.value),

    coupon: (props: { code: string | number }): boolean => /^(.)+$/.test(props.code + ''),

    len: (props: { value: string; minLength: number | string }): boolean => {
      const minLength = parseInt(String(props.minLength), 10) || 1;
      return typeof props.value === 'string' && props.value.length > minLength - 1;
    },

    alpha: (value: string): boolean => {
      const val = value ? value : '';
      return /^[A-Za-z\d\s]+$/.test(val);
    },

    dateAfterYesterday: (date: { value: Date }): boolean => {
      const millisInDay = 86400000;
      const yesterday = new Date(new Date().getTime() - millisInDay);
      try {
        return date.value.getTime() > yesterday.getTime();
      } catch (_e) {
        // invalid date
      }
      return false;
    },
  };
}
